/**
 * Settings config API + TanStack Query hooks (Prompt 11F.8 / D14).
 *
 * Hand-declared types (not sourced from `shared/api-types`) — matches the
 * precedent in `@features/attendance/api/leaves.ts`.
 *
 * Endpoints:
 *   GET/PATCH /v1/config/organisation           — org-level (owner only)
 *   PATCH     /v1/config/organisation/override   — per-tenant override (owner only)
 *   GET/PATCH /v1/config/tax                     — always per-tenant (owner only)
 *   GET/PATCH /v1/config/school                  — always per-tenant (owner/admin)
 *   GET/PATCH /v1/config/notifications            — always per-tenant (owner/admin)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, tenantHeaderOverride } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';
import { useTenantSafeQuery } from '@core/tenant/useTenantSafeQuery';

export interface OrganisationConfig {
  legal_name: string;
  llpin: string | null;
  pan: string | null;
  registered_address: string | null;
  authorised_signatory: string | null;
  logo_url: string | null;
  support_email: string | null;
  support_phone: string | null;
}

export type OrganisationConfigUpdate = Partial<OrganisationConfig>;

export interface TaxConfig {
  gstin: string | null;
  gst_state_code: string | null;
  place_of_business: string | null;
  invoice_prefix: string | null;
  default_gst_rate: number;
  sac_code: string | null;
  gst_registered: boolean;
}

export type TaxConfigUpdate = Partial<TaxConfig>;

export interface SchoolConfig {
  school_name: string | null;
  website: string | null;
  timezone: string;
}

export type SchoolConfigUpdate = Partial<SchoolConfig>;

export interface NotificationsConfig {
  flags: {
    email_enabled: boolean;
    email_trial_booking_admin: boolean;
    email_booking_confirmation: boolean;
    email_invoice_issued: boolean;
    email_payment_received: boolean;
    email_class_cancelled: boolean;
    [k: string]: boolean;
  };
}

export type NotificationsConfigUpdate = Partial<NotificationsConfig['flags']>;

// Every key includes the active tenant id — these modules are always
// per-tenant (organisation's org-level layer is inherited, but the
// resolved/merged value returned still differs per caller). Without this,
// switching schools via the 11E.5 owner switcher would keep showing the
// PREVIOUS school's cached values: TenantContext's `queryClient.clear()`
// does not reliably force an immediate refetch of an already-mounted
// `useQuery` on its own — @components/entity's `useTenantAwareEntityList`
// establishes the same fix (tenant id folded into the query key).
export const settingsConfigKeys = {
  organisation: (tid: string) => ['config', 'organisation', tid] as const,
  tax: (tid: string) => ['config', 'tax', tid] as const,
  school: (tid: string) => ['config', 'school', tid] as const,
  notifications: (tid: string) => ['config', 'notifications', tid] as const,
};

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

export function useOrganisationConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: settingsConfigKeys.organisation(selectedTenantId),
    queryFn: () =>
      api.get<OrganisationConfig>(
        '/v1/config/organisation',
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateOrganisationConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: OrganisationConfigUpdate) =>
      api.patch<OrganisationConfig>(
        '/v1/config/organisation',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(settingsConfigKeys.organisation(selectedTenantId), data),
  });
}

export function useUpdateOrganisationOverride() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: OrganisationConfigUpdate) =>
      api.patch<OrganisationConfig>(
        '/v1/config/organisation/override',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(settingsConfigKeys.organisation(selectedTenantId), data),
  });
}

// ---------------------------------------------------------------------------
// Tax
// ---------------------------------------------------------------------------

export function useTaxConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: settingsConfigKeys.tax(selectedTenantId),
    queryFn: () =>
      api.get<TaxConfig>('/v1/config/tax', tenantHeaderOverride(selectedTenantId, isAllMode)),
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useUpdateTaxConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: TaxConfigUpdate) =>
      api.patch<TaxConfig>(
        '/v1/config/tax',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(settingsConfigKeys.tax(selectedTenantId), data),
  });
}

// ---------------------------------------------------------------------------
// School
// ---------------------------------------------------------------------------

export function useSchoolConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: settingsConfigKeys.school(selectedTenantId),
    queryFn: () =>
      api.get<SchoolConfig>(
        '/v1/config/school',
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSchoolConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: SchoolConfigUpdate) =>
      api.patch<SchoolConfig>(
        '/v1/config/school',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(settingsConfigKeys.school(selectedTenantId), data),
  });
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function useNotificationsConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: settingsConfigKeys.notifications(selectedTenantId),
    queryFn: () =>
      api.get<NotificationsConfig>(
        '/v1/config/notifications',
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateNotificationsConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: NotificationsConfigUpdate) =>
      api.patch<NotificationsConfig>(
        '/v1/config/notifications',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) =>
      qc.setQueryData(settingsConfigKeys.notifications(selectedTenantId), data),
  });
}
